import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { useWebSocket } from "@/lib/ws";
import { api } from "@/lib/api";
import LeftSidebar from "@/components/LeftSidebar";
import PeoplePanel from "@/components/PeoplePanel";
import OfficeMap from "@/components/OfficeMap";
import ChannelView from "@/components/ChannelView";
import DmView from "@/components/DmView";
import TasksView from "@/components/TasksView";
import ProjectsView from "@/components/ProjectsView";
import RequestsView from "@/components/RequestsView";
import OfficeStudio from "@/pages/OfficeStudio";
import NotificationsView from "@/components/NotificationsView";
import AdminView from "@/components/AdminView";
import BetaSummary from "@/components/BetaSummary";
import WaitlistLeadsView from "@/components/WaitlistLeadsView";
import DepartmentsView from "@/components/DepartmentsView";
import MentionsView from "@/components/MentionsView";
import SavedView from "@/components/SavedView";
import SearchView from "@/components/SearchView";
import HomeView from "@/components/HomeView";
import StandupView from "@/components/StandupView";
import IncomingHuddleBanner from "@/components/IncomingHuddleBanner";
import DesktopNotifsPrompt from "@/components/DesktopNotifsPrompt";
import RightDrawer, { DrawerCtx } from "@/components/RightDrawer";
import CallOverlay from "@/components/CallOverlay";
import AvatarSetup from "@/components/AvatarSetup";
import ActivationChecklist from "@/components/ActivationChecklist";
import useDocumentTitle from "@/lib/useDocumentTitle";
import { playMessagePing } from "@/lib/soundEffects";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as log from "@/lib/logger";


export default function AppShell() {
  const { currentOrg, user, refreshMe } = useAuth();
  const location = useLocation();
  const [presence, setPresence] = useState({});
  const [events, setEvents] = useState({ msgs: 0, tasks: 0, ships: 0 });
  const [notifBump, setNotifBump] = useState(0);
  const [drawer, setDrawer] = useState({ open: false, kind: null, id: null, data: null });
  const [activeCall, setActiveCall] = useState(null);

  const openDrawer = useCallback((kind, id, data) => setDrawer({ open: true, kind, id, data }), []);
  const closeDrawer = useCallback(() => setDrawer((d) => ({ ...d, open: false })), []);
  const wsBroadcastRef = useRef(null);

  const onEvent = useCallback((data) => {
    if (data.type === "presence.snapshot") {
      const map = {};
      for (const u of data.users) map[u.user_id] = u;
      setPresence(map);
    } else if (data.type === "presence.update") {
      setPresence((p) => ({ ...p, [data.user.user_id]: { ...p[data.user.user_id], ...data.user } }));
    } else if (data.type === "message.new") {
      setEvents((e) => ({ ...e, msgs: e.msgs + 1 }));
      // Play a soft ping for real chat messages from OTHERS (not our own
      // echoes). Muted when the tab is already focused so we don't chime
      // while the user is actively reading.
      const from = data.message?.user_id;
      const isMine = from && from === user?.id;
      const tabFocused = typeof document !== "undefined" && document.hasFocus();
      if (!isMine && !tabFocused) playMessagePing();
      wsBroadcastRef.current && wsBroadcastRef.current("message.new", data.message);
    } else if (data.type === "message.update" || data.type === "message.delete" || data.type === "message.reaction") {
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
    } else if (data.type === "task.created" || data.type === "task.updated" || data.type === "task.deleted") {
      setEvents((e) => ({ ...e, tasks: e.tasks + 1 }));
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
    } else if (data.type === "shipment.created" || data.type === "shipment.updated" || data.type === "shipment.deleted") {
      setEvents((e) => ({ ...e, ships: e.ships + 1 }));
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
    } else if (data.type === "escalation.created" || data.type === "escalation.updated") {
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
    } else if (data.type === "project.created" || data.type === "project.updated" || data.type === "project.archived") {
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
    } else if (data.type === "request.created" || data.type === "request.updated") {
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
    } else if (data.type === "dm.new") {
      setNotifBump((n) => n + 1);
      // DM ping — mirror the channel-message rule (skip our own echoes and
      // skip when the tab is already focused).
      const from = data.message?.user_id;
      const isMine = from && from === user?.id;
      const tabFocused = typeof document !== "undefined" && document.hasFocus();
      if (!isMine && !tabFocused) playMessagePing();
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
    } else if (data.type === "call.started" || data.type === "call.updated") {
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
    } else if (data.type === "channel.read" || data.type === "conversation.read") {
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
    } else if (data.type === "office.reaction" || data.type === "office.published" || data.type === "office.restored" || data.type === "office.seat_claimed" || data.type === "office.seat_released") {
      // Passthrough for Virtual Office real-time events (floating reactions,
      // layout publish/restore). OfficeMap subscribes and reacts.
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
    } else if (data.type === "user.updated") {
      wsBroadcastRef.current && wsBroadcastRef.current(data.type, data);
      // Mirror the updated user into the presence store so avatar changes
      // propagate to office / people views without waiting for a refetch.
      setPresence((prev) => {
        const u = data.user || {};
        if (!u.id) return prev;
        const cur = prev[u.id];
        if (!cur) return prev;
        return { ...prev, [u.id]: { ...cur,
          name: u.name || cur.name,
          avatar_url: u.avatar_url,
          avatar_type: u.avatar_type,
          avatar_seed: u.avatar_seed,
          avatar_config: u.avatar_config,
          profile_photo_url: u.profile_photo_url,
          short_status: u.short_status,
          updated_at: u.updated_at,
        } };
      });
      // If it's ME (other tab), refresh my auth user so the current-user avatar updates too.
      if (data.user?.id && data.user.id === user?.id) {
        refreshMe && refreshMe();
      }
    }
  }, [refreshMe, user?.id]);

  const { connected, send } = useWebSocket(currentOrg?.id, onEvent);

  // Belt-and-suspenders presence sync: refetch `GET /api/presence` every 25s
  // AND every time the WebSocket reconnects. This guarantees other people
  // show up as "online" even if a `presence.snapshot` event was dropped
  // (transient network hiccup, proxy timeout, etc.) — so an atasan never
  // sees a bawahan as offline when they're actually working.
  useEffect(() => {
    if (!currentOrg) return;
    let alive = true;
    const refresh = async () => {
      try {
        const { data } = await api.get("/presence");
        if (!alive || !Array.isArray(data)) return;
        setPresence((prev) => {
          // Merge over what we already have so local optimistic bits (my own
          // avatar move, status pick) aren't clobbered by a slightly older
          // server view.
          const next = { ...prev };
          for (const u of data) {
            if (!u?.user_id) continue;
            next[u.user_id] = { ...prev[u.user_id], ...u };
          }
          return next;
        });
      } catch { /* transient — the WS snapshot / next tick will heal us */ }
    };
    refresh(); // immediate fetch on org switch / mount
    const iv = setInterval(refresh, 25_000);
    return () => { alive = false; clearInterval(iv); };
  }, [currentOrg]);

  // Extra safety: on any WS transition to `connected=true` (initial connect
  // OR a reconnect after a drop), pull a fresh presence snapshot so the UI
  // never lingers on stale offline dots after a network blip.
  useEffect(() => {
    if (!connected || !currentOrg) return;
    api.get("/presence")
      .then(({ data }) => {
        if (!Array.isArray(data)) return;
        setPresence((prev) => {
          const next = { ...prev };
          for (const u of data) if (u?.user_id) next[u.user_id] = { ...prev[u.user_id], ...u };
          return next;
        });
      })
      .catch(() => {});
  }, [connected, currentOrg]);

  // People panel state
  const [peopleOpen, setPeopleOpen] = useState(false);

  // Heartbeat + activity tracking (Gather Town model).
  // As long as the tab is open (even backgrounded/hidden), we keep sending
  // heartbeats so the user stays "online". Only when the tab is actually
  // closed does the WS drop and backend flip the user to "offline".
  //
  // Auto-away still works while the tab is visible: if the user hasn't
  // moved the mouse / typed for >60s, we send `active=false` and the
  // server will flip them to "away" after AWAY_AFTER_SECONDS.
  useEffect(() => {
    if (!currentOrg) return;
    let activity = Date.now();
    const bump = () => { activity = Date.now(); };
    window.addEventListener("mousemove", bump);
    window.addEventListener("keydown", bump);
    window.addEventListener("click", bump);

    const isTabVisible = () => typeof document !== "undefined" && document.visibilityState === "visible";
    const sendBeat = () => {
      // active=true when tab is hidden (Gather Town: backgrounded ≠ away)
      // or when the user interacted in the last 60s while the tab is visible.
      const active = !isTabVisible() || (Date.now() - activity < 60_000);
      api.post("/presence/heartbeat", { active }).catch(() => {});
    };

    // Fire once immediately so the newly connected session refreshes presence
    // right away instead of waiting up to 20s for the first tick.
    sendBeat();
    const iv = setInterval(sendBeat, 20_000);

    const onVisibility = () => {
      // Coming back to the tab counts as fresh activity; send an immediate
      // heartbeat so the "away" state clears without waiting for the interval.
      if (isTabVisible()) activity = Date.now();
      sendBeat();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("mousemove", bump);
      window.removeEventListener("keydown", bump);
      window.removeEventListener("click", bump);
    };
  }, [currentOrg]);

  // Custom event bridge so PeoplePanel/OfficeMap can open a call
  useEffect(() => {
    const h = (e) => setActiveCall(e.detail);
    window.addEventListener("tovra:open-call", h);
    return () => window.removeEventListener("tovra:open-call", h);
  }, []);

  // Global ESC to close drawer/people/call
  useEffect(() => {
    const h = (e) => {
      if (e.key !== "Escape") return;
      if (activeCall) { setActiveCall(null); return; }
      if (drawer.open) { closeDrawer(); return; }
      if (peopleOpen) { setPeopleOpen(false); return; }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [activeCall, drawer.open, peopleOpen, closeDrawer]);

  // subscribe helper
  const subscribersRef = useRef(new Set());
  wsBroadcastRef.current = (type, payload) => {
    for (const cb of subscribersRef.current) {
      try { cb(type, payload); }
      catch (err) { log.debug("[appshell] subscriber threw", err); }
    }
  };
  const subscribe = useCallback((cb) => {
    subscribersRef.current.add(cb);
    return () => subscribersRef.current.delete(cb);
  }, []);

  const drawerCtxVal = useMemo(() => ({ openDrawer, closeDrawer, drawer }), [drawer, openDrawer, closeDrawer]);

  // Reflect the user's total unread count in the browser tab title so
  // background tabs still surface pending work ("TOVRA · 3 unread").
  useDocumentTitle({ subscribe, currentOrgId: currentOrg?.id, userId: user?.id });

  // First-time avatar prompt: shown once per browser to users who haven't set an avatar_type yet.
  const [avatarPromptOpen, setAvatarPromptOpen] = useState(false);
  useEffect(() => {
    if (!user) return;
    if (user.avatar_type) return;
    // NOTE: localStorage here stores a non-sensitive UI-tour flag ("1" once the
    // dialog has been shown). No tokens, no PII. Static analyzers flag any
    // localStorage use as "sensitive-data storage" — this one is safe.
    const key = `tovra_avatar_prompted_${user.id}`;
    if (localStorage.getItem(key)) return;
    setAvatarPromptOpen(true);
    localStorage.setItem(key, "1");
  }, [user?.id, user?.avatar_type]);

  // Ref shared with LeftSidebar so ActivationChecklist can open the invite dialog.
  const inviteRef = useRef(null);

  // Track first Virtual Office visit for the activation checklist (idempotent).
  useEffect(() => {
    if (!currentOrg?.id) return;
    if (!location.pathname.startsWith("/app/office")) return;
    api.post("/activation-checklist/office-visited").catch(() => {});
    // Analytics event for closed-beta telemetry.
    import("@/lib/analytics").then(({ track }) => track("virtual_office_entered")).catch(() => {});
  }, [currentOrg?.id, location.pathname]);

  // Show a lightweight loading gate while switching workspaces so the previous
  // organization's data never bleeds into the new one visually.
  const [orgSwitching, setOrgSwitching] = useState(false);
  const lastOrgIdRef = useRef(currentOrg?.id);
  useEffect(() => {
    if (!currentOrg?.id) return;
    if (lastOrgIdRef.current && lastOrgIdRef.current !== currentOrg.id) {
      // Reset any stale in-memory data from the previous org so nothing bleeds through.
      setPresence({});
      setEvents({ msgs: 0, tasks: 0, ships: 0 });
      setActiveCall(null);
      setDrawer({ open: false, kind: null, id: null, data: null });
      setOrgSwitching(true);
      const t = setTimeout(() => setOrgSwitching(false), 400);
      lastOrgIdRef.current = currentOrg.id;
      return () => clearTimeout(t);
    }
    lastOrgIdRef.current = currentOrg.id;
  }, [currentOrg?.id]);

  return (
    <DrawerCtx.Provider value={drawerCtxVal}>
      <div className="flex h-screen w-screen overflow-hidden bg-[var(--bg-app)]" key={currentOrg?.id || "no-org"} data-testid="app-shell">
        <LeftSidebar presence={presence} connected={connected} onWsSend={send} notifBump={notifBump} subscribe={subscribe} onOpenPeople={() => setPeopleOpen(true)} onInviteOpenRef={inviteRef}/>
        <main className="flex-1 flex overflow-hidden">
          <div className="flex-1 overflow-hidden flex flex-col">
            {orgSwitching ? (
              <div className="flex-1 flex items-center justify-center bg-white" data-testid="workspace-loading">
                <div className="text-sm text-zinc-500">Loading workspace…</div>
              </div>
            ) : (
            <Routes>
              <Route path="office" element={<OfficeMap presence={presence} wsSend={send} subscribe={subscribe} connected={connected} onOpenCall={setActiveCall} onOpenPeople={() => setPeopleOpen(true)}/>} />
              <Route path="home" element={<HomeView onOpenPeople={() => setPeopleOpen(true)} presence={presence}/>} />
              <Route path="mentions" element={<MentionsView />} />
              <Route path="saved" element={<SavedView />} />
              <Route path="search" element={<SearchView />} />
              <Route path="channel/:channelId" element={<ChannelView subscribe={subscribe} wsSend={send} onOpenCall={setActiveCall}/>} />
              <Route path="dm/:convId" element={<DmView subscribe={subscribe} onOpenCall={setActiveCall} presence={presence}/>} />
              <Route path="dm" element={<DmView subscribe={subscribe} onOpenCall={setActiveCall} presence={presence}/>} />
              <Route path="tasks" element={<TasksView subscribe={subscribe} />} />
              <Route path="standup" element={<StandupView subscribe={subscribe}/>} />
              <Route path="projects" element={<ProjectsView subscribe={subscribe} />} />
              <Route path="requests" element={<RequestsView subscribe={subscribe} />} />
              <Route path="settings/office-studio" element={<OfficeStudio />} />
              <Route path="notifications" element={<NotificationsView />} />
              <Route path="admin" element={<AdminView />} />
              <Route path="beta" element={<BetaSummary />} />
              <Route path="waitlist" element={<WaitlistLeadsView />} />
              <Route path="departments" element={<DepartmentsView />} />
              <Route path="*" element={<Navigate to="office" replace />} />
            </Routes>
            )}
          </div>
          <RightDrawer />
        </main>
        <PeoplePanel open={peopleOpen} onClose={() => setPeopleOpen(false)} presenceMap={presence}/>
        {activeCall && <CallOverlay call={activeCall} onClose={() => setActiveCall(null)} />}
        <IncomingHuddleBanner
          subscribe={subscribe}
          currentUserId={user?.id}
          activeCallId={activeCall?.id}
          onJoin={(call) => setActiveCall(call)}
        />
        <DesktopNotifsPrompt />

        <ActivationChecklist onInvite={() => inviteRef.current && inviteRef.current()} />

        <Dialog open={avatarPromptOpen} onOpenChange={setAvatarPromptOpen}>
          <DialogContent className="max-w-3xl" data-testid="first-time-avatar-dialog" aria-describedby="first-time-avatar-desc">
            <DialogHeader>
              <DialogTitle>Welcome — set your avatar</DialogTitle>
              <DialogDescription id="first-time-avatar-desc">
                Upload a profile photo or design a friendly illustrated avatar. You can change this any time from Profile settings.
              </DialogDescription>
            </DialogHeader>
            <AvatarSetup onSaved={() => setAvatarPromptOpen(false)} onCancel={() => setAvatarPromptOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
    </DrawerCtx.Provider>
  );
}
